<template>
  <div class="xmap">
    <div class="menu">
      <popup
        component-path="pages/fences/crosser"
        className="link"
        width="800px"
        height="800px"
        :pivot-y="0"
      >
        <div class="svg" v-html="menuSvgRaw.fences" />
        Fences
      </popup>
      <popup
        componentPath="pages/drivers/routes"
        className="link"
        width="85%"
        :pivot-y="0"
        :options="{ date: date }"
      >
        <div class="svg" v-html="menuSvgRaw.routes" />
        Route-1-2-3
      </popup>
      <el-popover
        placement="top"
        width="200"
        trigger="click"
        popper-class="map-filters"
      >
        <div class="title">Map</div>
        <el-checkbox-group v-model="spotTypes" size="mini">
          <el-checkbox
            v-for="type in options.spotTypes"
            :label="type"
            :key="type"
            border
            class="w-full"
          >
            {{ type.capitalize() }}
          </el-checkbox>
        </el-checkbox-group>
        <div class="title">Dispatch</div>
        <div class="el-checkbox-group">
          <el-checkbox
            v-model="hos"
            size="mini"
            class="w-full"
            border
            :disabled="hosLoading"
            @change="triggleHos"
          >
            Driver HOS
          </el-checkbox>
          <el-checkbox
            v-model="dispatchDetails"
            size="mini"
            class="w-full"
            border
          >
            Location Names
          </el-checkbox>
        </div>
        <a slot="reference" class="link">
          <div class="svg" v-html="menuSvgRaw.filters" />
          Filters
        </a>
      </el-popover>
      <a
        class="link relative"
        :class="{ active: dispatchView }"
        @click="toggleDispatch"
      >
        <div class="svg" v-html="menuSvgRaw.dispatch" />
        Dispatch
        <sup class="text-2xs absolute top-0 right-0 text-green-500">
          {{ dispatchedRoutes.length }}
        </sup>
      </a>
      <a v-if="undispatchedRoutes.length == 0" class="link" :disabled="true">
        <div class="svg" v-html="menuSvgRaw.pending" />
        Available
      </a>
      <a
        v-else
        class="link relative"
        :class="{ active: openView }"
        @click="openView = !openView"
      >
        <div class="svg" v-html="menuSvgRaw.pending" />
        Available
        <sup class="text-2xs absolute top-0 right-0 text-red-500">
          {{ undispatchedRoutes.length }}
        </sup>
      </a>
    </div>
    <div class="map-view" ref="map">
      <div class="dispatch panel" ref="dispatch">
        <v-table
          :items="truckers"
          :default-sort="{}"
          :cell-class="cellClass"
          :row-height="50"
          :get-height="getHeight"
        >
          <template v-slot:tbody>
            <u-table-column
              label=""
              fixed="left"
              align="left"
              width="250"
              class-name="left"
            >
              <template v-slot:header>
                <div class="flex flex-col divide-y divide-gray-800">
                  <div class="flex items-center justify-between h-6">
                    <div class="w-5">&nbsp;</div>
                    <a
                      class="el-icon-d-arrow-left text-base"
                      @click="previousDay"
                    />
                    <div class="flex items-center gap-1">
                      <span>{{ weekday }}</span>
                      <el-date-picker
                        class="text"
                        size="mini"
                        v-model="date"
                        type="date"
                        format="MM/dd"
                        :value-format="'yyyy-MM-dd'"
                        :clearable="false"
                        @change="debouncedDateChanged"
                        style="width: 80px"
                      />
                    </div>
                    <a
                      class="el-icon-d-arrow-right text-base"
                      @click="nextDay"
                    />
                    <div class="routes-count">
                      {{ dispatchedRoutes.length }}
                    </div>
                  </div>
                </div>
              </template>
              <template v-slot="scope">
                <div class="panel" :key="scope.row.id">
                  <div class="flex items-cneter justify-between">
                    <div class="flex items-center gap-1">
                      <div class="flex items-center scale-90">
                        <driver-duty :driver="vacation(scope.row)" />
                        <a :class="engineStates[scope.row.engine_state]" />
                        <div class="relative">
                          <el-tooltip
                            v-if="activeGps(scope.row)"
                            :content="scope.row.location.time"
                            placement="left"
                          >
                            <a
                              @click="focusDriverMarker(scope.row)"
                              class="fas fa-location-arrow green"
                            />
                          </el-tooltip>
                          <a
                            v-else
                            @click="focusDriverMarker(scope.row)"
                            class="fas fa-location-arrow gray"
                          />
                          <div
                            v-if="activeGps(scope.row)"
                            class="absolute -bottom-0.5 -right-1 text-2xs font-bold"
                          >
                            {{ gpsSource(scope.row) }}
                          </div>
                        </div>
                      </div>
                      <a @click="openActivity(scope.row)" class="driver-name">
                        {{ scope.row.name }}
                      </a>
                    </div>
                    <div class="flex items-center gap-1">
                      <i
                        class="fas fa-circle-exclamation text-yellow-300 text-sm"
                        v-if="driverRouteAlert(scope.row)"
                      />
                      <div class="routes-count">
                        {{ driverRoutes(scope.row).length }}
                      </div>
                    </div>
                  </div>
                  <div v-if="!isEmptyData(scope.row.hos_logs)">
                    <div class="hos-logs">
                      <div>
                        <b>BREAK</b>
                        <div>{{ scope.row.hos_logs.available_time.break }}</div>
                      </div>
                      <div>
                        <b>DRIVE</b>
                        <div>{{ scope.row.hos_logs.available_time.drive }}</div>
                      </div>
                      <div>
                        <b>SHIFT</b>
                        <div>{{ scope.row.hos_logs.available_time.shift }}</div>
                      </div>
                      <div>
                        <b>TODAY</b>
                        <div>{{ scope.row.hos_logs.available_time.today }}</div>
                      </div>
                      <div>
                        <b>TMRW.</b>
                        <div>
                          {{ scope.row.hos_logs.available_time.tomorrow }}
                        </div>
                      </div>
                      <div>
                        <b>CYCLE</b>
                        <div>{{ scope.row.hos_logs?.cycle }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </u-table-column>
            <u-table-column
              :width="timelineColumnWidth"
              header-align="center"
              align="left"
              class-name="right"
            >
              <template v-slot:header>
                <div class="timelines" :class="{ details: dispatchDetails }">
                  <slot v-for="name in timelines">
                    <div :key="name" :style="{ width: timelineWidth + 'px' }">
                      <div class="hour">{{ name }}</div>
                      <div class="scale"><div /><div /></div>
                    </div>
                  </slot>
                </div>
              </template>
              <template v-slot="scope">
                <div
                  class="timeline-cell"
                  :class="{ details: dispatchDetails }"
                >
                  <XmapRoute
                    v-for="transport in driverRoutes(scope.row)"
                    :route="transport"
                    :details="dispatchDetails"
                    :timelineWidth="timelineWidth"
                    :key="transport.id"
                    @openRoute="openRoute"
                  />
                </div>
              </template>
            </u-table-column>
          </template>
        </v-table>
      </div>
      <div class="order panel" ref="order">
        <XmapOrder v-if="activeRoute" :route="activeRoute" @close="close" />
      </div>
      <div class="spot panel" ref="spot">
        <XmapSpot
          v-if="activeSpot"
          :hub="hub"
          :date="date"
          :spot="activeSpot"
          @openRoute="openRoute"
          @close="close"
        />
      </div>
      <div class="open panel" ref="open">
        <slot v-if="openView">
          <div class="undispatched" v-if="!isEmptyData(undispatchedRoutes)">
            <div class="title">
              <div>Pending</div>
              <div>{{ undispatchedRoutes.length }}</div>
            </div>
            <div class="trips">
              <div
                v-for="transport in undispatchedRoutes"
                :key="transport.id"
                class="trip"
              >
                <div class="header">
                  <div class="flex items-center gap-0.5">
                    <div> {{ transport.container.abbrev_type }} </div>
                    <popup
                      className="no-underline"
                      componentPath="components/html"
                      width="900px"
                      :pivot-y="0"
                      :options="viewOrderOptions(transport.container.id)"
                    >
                      {{ transport.container.id }}
                    </popup>
                    <sup class="text-2xs">{{ transport.container.ctype }}</sup>
                  </div>
                  <div class="flex items-center gap-1">
                    <div class="truncate w-36 text-right">
                      {{ transport.customer.name }}
                    </div>
                    <a @click="renderRouting(transport)">
                      <i
                        class="fas fa-route text-blue-500"
                        :class="{ 'text-green-500': currentRoute == transport }"
                      />
                    </a>
                  </div>
                </div>
                <XmapRoute
                  :route="transport"
                  :details="true"
                  :timelineWidth="transport.operations.length > 2 ? 96 : 180"
                  :key="transport.id"
                  mode="pending"
                  @openRoute="openRoute"
                />
              </div>
            </div>
          </div>
          <div class="brokered" v-if="!isEmptyData(brokeredRoutes)">
            <div class="title">
              <div>Brokered</div>
              <div>{{ brokeredRoutes.length }}</div>
            </div>
            <div class="trips">
              <div
                v-for="transport in brokeredRoutes"
                :key="transport.id"
                class="trip"
              >
                <div class="header">
                  <div>
                    #{{ transport.container.id }}
                    {{ transport.customer.name }}
                  </div>
                  <a class="fas fa-route" />
                </div>
                <XmapRoute
                  :route="transport"
                  :details="true"
                  :timelineWidth="96"
                  :key="transport.id"
                  mode="brokered"
                  @openRoute="openRoute"
                />
              </div>
            </div>
          </div>
        </slot>
      </div>
    </div>
    <popup
      ref="activity"
      componentPath="pages/drivers/activity"
      width="400px"
      :pivot-y="0"
      :resizable="false"
      :options="driverActivityOptions"
    />
    <div id="tooltip" v-if="markerName" v-html="markerName" />
    <chat-popup />
  </div>
</template>
<script>
import api from "@/default/utils/api";
import { debounce } from "lodash";
import { format, parse, addDays } from "date-fns";
import truckOnlySvgRaw from "@/images/truck-only2.svg?raw";
import truckChassisSvgRaw from "@/images/truck-chassis2.svg?raw";
import fullLoadSvgRaw from "@/images/full-load2.svg?raw";
import emptyLoadSvgRaw from "@/images/empty-load2.svg?raw";
import fencesSvgRaw from "@/images/xmap-fences.svg?raw";
import filtersSvgRaw from "@/images/xmap-filters.svg?raw";
import pendingSvgRaw from "@/images/xmap-pending.svg?raw";
import dispatchSvgRaw from "@/images/xmap-dispatch.svg?raw";
import routesSvgRaw from "@/images/xmap-routes.svg?raw";
import fitSvgRaw from "@/images/xmap-fit.svg?raw";
import XmapOrder from "@/default/pages/xmap/order.vue";
import XmapRoute from "@/default/pages/xmap/route.vue";
import XmapSpot from "@/default/pages/xmap/spot.vue";
export default {
  name: "XMap",
  mixins: [api],
  components: { XmapOrder, XmapRoute, XmapSpot },
  props: {
    hub: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      engine: {},
      hm: {},
      gm: {},
      filter: {},
      options: {
        spotTypes: ["terminal", "depot", "yard", "warehouse"],
      },
      date: null,
      spots: [],
      truckers: [],
      locations: [],
      routes: [],
      hosLogs: [],
      markerName: null,
      activeTrucker: null,
      timestamp: new Date().getTime(),
      intervalId: null,
      hosTimeoutId: null,
      mapClickHandler: null,
      mousemoveHandler: null,
      resizeHandler: null,
      cursorMousemoveHandler: null,
      activeRoute: null,
      activeSpot: null,
      currentRoute: null,
      engineStates: {
        on: "fas fa-circle-play green",
        off: "fas fa-circle-stop gray",
        paused: "fas fa-circle-pause brown",
        null: "fas fa-circle-stop gray",
      },
      truckStatusSvgRaw: {
        "truck-only": truckOnlySvgRaw,
        "truck-chassis": truckChassisSvgRaw,
        "full-load": fullLoadSvgRaw,
        "empty-load": emptyLoadSvgRaw,
      },
      menuSvgRaw: {
        fences: fencesSvgRaw,
        routes: routesSvgRaw,
        filters: filtersSvgRaw,
        pending: pendingSvgRaw,
        dispatch: dispatchSvgRaw,
        fit: fitSvgRaw,
      },
      spotTypes: ["terminal", "depot", "yard", "warehouse"],
      openView: false,
      dispatchView: false,
      dispatchDetails: false,
      hos: false,
      hosLoading: false,
      debouncedDateChanged: null,
      format,
      parse,
      addDays,
    };
  },
  async mounted() {
    this.$actions.setHubId(this.hub.id);
    this.debouncedDateChanged = debounce(this.dateChanged, 500);
    this.date = format(new Date(), "yyyy-MM-dd");
    await Promise.all([
      this.loadOptions(),
      this.loadEngine(),
      this.loadRoutes(),
    ]);
    this.stopBack();
    this.autoTooltip();
    this.intervalClock();
    this.cursorTimeline();
    this.mapViewClick();
    window.XMap = {
      receive: this.receive,
      reloadData: this.reloadData,
    };
  },
  beforeDestroy() {
    window.XMap = undefined;
    window.clearInterval(this.intervalId);
    if (this.mapClickHandler) {
      this.$refs.map.removeEventListener("click", this.mapClickHandler);
    }
    if (this.mousemoveHandler) {
      window.removeEventListener("mousemove", this.mousemoveHandler);
    }
    if (this.cursorMousemoveHandler) {
      document.removeEventListener("mousemove", this.cursorMousemoveHandler);
    }
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler);
    }
  },
  watch: {
    async spotTypes(nv, ov) {
      if (nv != ov) {
        await this.addSpotsToMap();
        await this.fitAllOnMap();
      }
    },
  },
  computed: {
    googleMap() {
      return /google/i.test(this.engine.name);
    },
    hereMap() {
      return /here/i.test(this.engine.name);
    },
    activeTruckers() {
      return this.truckers.filter((o) => o.location.time);
    },
    activeGps() {
      return (trucker) => {
        const { sssepoch } = trucker.location;
        if (!sssepoch) return false;
        return this.timestamp - sssepoch * 1000 <= 300 * 1000;
      };
    },
    driverActivityOptions() {
      return { id: this.activeTrucker?.id, name: this.activeTrucker?.name };
    },
    gpsSource() {
      return (trucker) => {
        const { source } = trucker.location;
        return (source || "").charAt(0).toUpperCase();
      };
    },
    driverRouteAlert() {
      return (trucker) => {
        return this.driverRoutes(trucker).some((route) =>
          route.operations.some((o) => {
            return (
              ![this.date, null].includes(o.date) ||
              (!o.arrived && o.timestamp < this.timestamp / 1000)
            );
          }),
        );
      };
    },
    driverRoutes() {
      return (trucker) => {
        return this.routes.filter((o) => o.trucker_id == trucker.id);
      };
    },
    dispatchedRoutes() {
      return this.routes.filter((o) => o.trucker_id);
    },
    undispatchedRoutes() {
      return this.routes.filter((o) => !o.trucker_id && !o.brokered);
    },
    brokeredRoutes() {
      return this.routes.filter((o) => o.brokered);
    },
    truckAction() {
      return (trucker) => {
        if (trucker.truck.action) {
          return `${trucker.truck.action} - ${trucker.truck.location}`;
        }
      };
    },
    vacation() {
      return (trucker) => {
        return {
          id: trucker.id,
          name: trucker.name,
          dispatched: !this.isEmptyData(this.driverRoutes(trucker)),
          ...trucker.vacation,
        };
      };
    },
    weekday() {
      return format(new Date(this.date + "T00:00:00"), "EEE");
    },
    timelines() {
      return Array.from({ length: 24 }, (_, index) => index);
    },
    timelineWidth() {
      return this.dispatchDetails ? 96 : 96;
    },
    timelineColumnWidth() {
      return this.timelineWidth * 24 + 32;
    },
  },
  methods: {
    cellClass({ row, column, rowIndex, columnIndex }) {
      return columnIndex == 0 ? this.vacation(row).status : 0;
    },
    getHeight() {
      return this.$refs.dispatch.offsetHeight - 10;
    },
    intervalClock() {
      this.timestamp = new Date().getTime();
      this.intervalId = window.setInterval(
        () => (this.timestamp = new Date().getTime()),
        15000,
      );
    },
    toggleDispatch() {
      const elem = this.$refs.dispatch;
      const mapWrapper = elem.parentElement.lastChild;
      if (this.dispatchView) {
        this.dispatchView = false;
        elem.classList.remove("fade-in-right");
        elem.classList.add("fade-out-right");
        mapWrapper.classList.remove("blur");
      } else {
        this.dispatchView = true;
        elem.classList.remove("fade-out-right");
        elem.classList.add("fade-in-right");
        mapWrapper.classList.add("blur");
        setTimeout(() => window.dispatchEvent(new Event("resize")), 100);
      }
    },
    async buildSpotSvg(content, color, number, current, dropped) {
      try {
        const orgUrl = "http://www.w3.org/2000/svg";
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(content, "image/svg+xml");
        const svgElement = svgDoc.documentElement;
        if (current) {
          svgElement.setAttribute("viewBox", "0 -10 36 36");
          svgElement.setAttribute("color", color);
        } else {
          svgElement.setAttribute("viewBox", "0 -16 48 48");
          svgElement.setAttribute("color", color);
          svgElement.setAttribute("opacity", "0.6");
        }

        if (dropped) {
          svgElement.setAttribute("color", "red");
        }

        if (number > 0) {
          // Create a group element to hold the bubble and the number
          const gElement = svgDoc.createElementNS(orgUrl, "g");

          // Apply a translation to the group element to move it vertically
          gElement.setAttribute("transform", "translate(0, -10)"); // Move up by 10 units

          // Create a circle element for the bubble
          const circleElement = svgDoc.createElementNS(orgUrl, "circle");
          circleElement.setAttribute("cx", "50%");
          circleElement.setAttribute("cy", "20%");
          circleElement.setAttribute("r", "6");
          circleElement.setAttribute("fill", "#FFFF00");
          circleElement.setAttribute("stroke", "#FF0000");
          circleElement.setAttribute("stroke-width", "0.5");

          // Create a text element for the number
          const textElement = svgDoc.createElementNS(orgUrl, "text");
          textElement.setAttribute("x", "50%");
          textElement.setAttribute("y", "20%");
          textElement.setAttribute("dy", "0.3em");
          textElement.setAttribute("text-anchor", "middle");
          textElement.setAttribute("font-size", "6pt");
          textElement.setAttribute("font-weight", "bold");
          textElement.setAttribute("font-family", "Arial");

          textElement.setAttribute("fill", "#FF0000");
          textElement.textContent = number;

          // Append the circle and text to the group element
          gElement.appendChild(circleElement);
          gElement.appendChild(textElement);

          // Append the group element to the SVG
          svgElement.appendChild(gElement);
        }

        // Serialize the SVG back to a string
        const serializer = new XMLSerializer();
        return serializer.serializeToString(svgElement);
      } catch (ex) {}
    },
    async loadOptions() {
      try {
      } catch (err) {}
    },
    async loadEngine() {
      try {
        this.engine = (await this.$http.get("/api/v1/maps/engine.json")).data;
        this.initMap();
        await this.loadSpots();
        await this.loadTruckers();
        await this.fitAllOnMap();
      } catch (err) {
        this.$message({ message: err?.response?.data?.errors, type: "error" });
      }
    },
    async reloadData(options = {}) {
      if (options.hub == this.hub.name && options.date == this.date) {
        if (options.target == "spots") await this.loadSpots();
        if (options.target == "truckers") await this.loadTruckers();
        if (options.target == "routes") await this.loadRoutes();
      }
    },
    async previousDay() {
      this.date = format(
        this.addDays(this.parse(this.date, "yyyy-MM-dd", new Date()), -1),
        "yyyy-MM-dd",
      );
      await this.debouncedDateChanged();
    },
    async nextDay() {
      this.date = format(
        this.addDays(this.parse(this.date, "yyyy-MM-dd", new Date()), 1),
        "yyyy-MM-dd",
      );
      await this.debouncedDateChanged();
    },
    async dateChanged() {
      await Promise.all([
        this.loadSpots(),
        this.loadRoutes(),
        this.loadTruckers(),
      ]);
    },
    async loadSpots() {
      try {
        const response = await this.$http.get("/api/v1/xmap/spots.json", {
          params: { hub_id: this.hub.id, date: this.date },
        });
        this.spots = response.data;
        await this.addSpotsToMap();
      } catch (err) {
        console.log(err);
      }
    },
    async loadTruckers() {
      try {
        const response = await this.$http.get("/api/v1/xmap/truckers.json", {
          params: { hub_id: this.hub.id, date: this.date },
        });
        this.truckers = response.data;
        this.addTruckersToMap();
        this.assignHosLogs();
      } catch (err) {}
    },
    async loadRoutes() {
      try {
        const response = await this.$http.get("/api/v1/xmap/routes.json", {
          params: { hub_id: this.hub.id, date: this.date },
        });
        this.routes = this.wrapperRoutes(response.data);
        setTimeout(() => window.dispatchEvent(new Event("resize")), 100);
      } catch (err) {
        console.log(err);
      }
    },
    async loadHos() {
      try {
        if (this.hos) {
          this.hosLoading = true;
          const response = await this.$http.get("/api/v1/xmap/hos.json", {
            params: { hub_id: this.hub.id },
          });
          this.hosLogs = response.data;
          this.assignHosLogs();
          this.hosTimeoutId = setTimeout(
            async () => await this.loadHos(),
            60 * 1000,
          );
        }
      } catch (err) {
      } finally {
        this.hosLoading = false;
      }
    },
    wrapperRoutes(routes) {
      Object.values(this._.groupBy(routes, "trucker_id")).forEach((group) => {
        var chosen = group[0];
        group.forEach((route) => {
          if (route.operations.some((o) => o.arrived)) {
            chosen = route;
          }
        });
        chosen.current = true;
      });
      return routes;
    },
    assignHosLogs() {
      if (this.hos) {
        this.hosLogs.forEach((o) => {
          let trucker = this.truckers.find((t) => t.id == o.id);
          if (trucker) this.$set(trucker, "hos_logs", o.hos_logs);
        });
      }
    },
    async triggleHos() {
      try {
        if (this.hos) {
          await this.loadHos();
        } else {
          this.truckers.forEach((o) => this.$set(o, "hos_logs", {}));
          clearTimeout(this.hosTimeoutId);
        }
      } catch (err) {}
    },
    initHereMap() {
      this.hm.platform = new H.service.Platform({
        apikey: this.engine.api_key,
      });
      var engineType = H.Map.EngineType["HARP"];
      var defaultLayers = this.hm.platform.createDefaultLayers({ engineType });
      this.hm.map = new H.Map(
        document.querySelector(".map-view"),
        defaultLayers.vector.normal.truck,
        {
          engineType,
          zoom: 12,
          center: { lat: this.engine.lat, lng: this.engine.lng },
        },
      );
      this.resizeHandler = () => this.hm.map.getViewPort().resize();
      window.addEventListener("resize", this.resizeHandler);
      new H.mapevents.Behavior(new H.mapevents.MapEvents(this.hm.map));
      this.hm.ui = H.ui.UI.createDefault(this.hm.map, defaultLayers);
      this.hm.ui.getControl("zoom").setAlignment("left-top");
      this.hm.ui.getControl("mapsettings").setAlignment("left-top");
      this.hm.ui.getControl("scalebar").setAlignment("left-bottom");

      let svgRaw = this.menuSvgRaw.fit;
      let fitAllOnMap = this.fitAllOnMap;

      class CustomControl extends H.ui.Control {
        renderInternal(el, doc) {
          let div = doc.createElement("div");
          div.classList.add("H_btn", "H_el");
          div.innerHTML = svgRaw;
          div.querySelector("svg").classList.add("H_icon");
          div.addEventListener("click", fitAllOnMap);
          el.appendChild(div);
          super.renderInternal(el, doc);
        }
      }
      const customControl = new CustomControl();
      this.hm.ui.addControl("customButton", customControl);
      this.hm.group = new H.map.Group();
    },
    async addSpotsToHereMap() {
      if (this.hm.spots) this.hm.group.removeObject(this.hm.spots);
      this.hm.spots = new H.map.Group();
      const spots = this.spots.filter((spot) =>
        this.spotTypes.includes(spot.type),
      );
      spots.forEach(async (spot) => {
        var { name, lat, lng, ctns, current, dropped } = spot;
        var svg = this.$getRawSvg(spot.type + "2");
        spot.svg = await this.buildSpotSvg(
          svg.path,
          svg.color,
          ctns,
          current,
          dropped,
        );
        var icon = new H.map.Icon(spot.svg, {
          size: { w: 48, h: 48 },
        });
        var marker = new H.map.Marker({ lat: lat, lng: lng }, { icon: icon });
        marker.addEventListener("tap", (evt) => {
          this.activeSpot = spot;
        });
        marker.addEventListener(
          "pointerenter",
          (evt) => {
            this.markerName = name;
          },
          false,
        );
        marker.addEventListener(
          "pointerleave",
          (evt) => {
            this.markerName = null;
          },
          false,
        );
        this.hm.spots.addObject(marker);
      });
      this.hm.group.addObject(this.hm.spots);
      this.hm.map.addObject(this.hm.group);
    },
    async renderRoutingToHereMap(route) {
      if (this.currentRoute == route) {
        if (this.hm.routing) this.hm.map.removeObject(this.hm.routing);
        this.hm.routing = null;
        this.currentRoute = null;
      } else {
        this.currentRoute = route;
        const [first, ...rest] = [...route.operations.map((o) => o.company)];
        const last = rest.pop();
        const routingParams = {
          routingMode: "fast",
          transportMode: "truck",
          origin: `${first.lat},${first.lng}`,
          destination: `${last.lat},${last.lng}`,
          return: "polyline,summary",
        };

        if (!this.isEmptyData(rest)) {
          routingParams.via = rest.map((o) => `${o.lat},${o.lng}`).join(";");
        }
        var viewPoints = [];
        var addMarker = (section, index) => {
          let label = index;
          let points = [section.departure, section.arrival];
          points.forEach((o, pindex) => {
            if (index == 0 || pindex > 0) {
              let svg = `<svg width="20" height="20" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="10" cy="10" r="10" stroke="white" fill="#ea4235"/>
                  <text x="10" y="14" font-size="10pt" font-family="Arial" text-anchor="middle" fill="white">
                    ${label + pindex + 1}
                  </text>
                </svg>`;
              let icon = new H.map.Icon(svg);
              let marker = new H.map.Marker(o.place.location, { icon });
              viewPoints.push(marker);
              if (pindex > 0) {
                marker.addEventListener(
                  "pointerenter",
                  (evt) => {
                    this.markerName = `
                      <div>Departure: ${section.departure.time}</div>
                      <div>Arrival: ${section.arrival.time}</div>
                      <div>Duration: ${(
                        section.summary.duration / 3600
                      ).toFixed(2)}hours</div>
                      <div>Trip: ${(section.summary.length / 1609.344).toFixed(
                        2,
                      )}miles</div>
                    `;
                  },
                  false,
                );

                marker.addEventListener(
                  "pointerleave",
                  (evt) => {
                    this.markerName = null;
                  },
                  false,
                );
              }
            }

            viewPoints;
          });
        };

        var addRouteLine = (section) => {
          let linestring = H.geo.LineString.fromFlexiblePolyline(
            section.polyline,
          );
          // Create a polyline to display the route:
          let routeOutline = new H.map.Polyline(linestring, {
            style: {
              lineWidth: 5,
              strokeColor: "#009e06",
              lineTailCap: "arrow-tail",
              lineHeadCap: "arrow-head",
            },
          });
          let routeArrows = new H.map.Polyline(linestring, {
            style: {
              lineWidth: 5,
              fillColor: "white",
              strokeColor: "white",
              lineDash: [0, 2],
              lineTailCap: "arrow-tail",
              lineHeadCap: "arrow-head",
            },
          });
          viewPoints.push(routeOutline, routeArrows);

          if (this.hm.routing) this.hm.map.removeObject(this.hm.routing);
          this.hm.routing = new H.map.Group();
          this.hm.routing.addObjects(viewPoints);
          this.hm.map.addObjects([this.hm.routing]);
          this.hm.map
            .getViewModel()
            .setLookAtData({ bounds: this.hm.routing.getBoundingBox() });
          setTimeout(() => {
            this.hm.map.setZoom(this.hm.map.getZoom() - 0.5);
          }, 250);
        };

        var addRouteShapeToMap = (result) => {
          result.routes[0].sections.forEach((section, index) => {
            addMarker(section, index);
            addRouteLine(section);
          });
        };

        const router = this.hm.platform.getRoutingService(null, 8);
        router.calculateRoute(
          routingParams,
          (result) => {
            addRouteShapeToMap(result);
          },
          (error) => {
            this.$message({ message: error.message, type: "error" });
          },
        );
      }
    },
    addTruckersToHereMap() {
      if (this.hm.truckers) this.hm.group.removeObject(this.hm.truckers);
      this.hm.truckers = new H.map.Group();
      this.activeTruckers.forEach((trucker) => {
        var { name, location } = trucker;
        var vstatus = this.vacation(trucker).status;
        var { lat, lng, heading, speed, timestamp } = location;
        var domIconDiv = document.createElement("div");
        var svgRotate = 0;
        var truckStatusSvg = this.truckStatusSvgRaw[trucker.truck];
        domIconDiv.innerHTML = `<a>${truckStatusSvg}</a>`;
        domIconDiv.classList.add("truck-icon");
        domIconDiv.classList.add(vstatus);
        var icon = new H.map.DomIcon(domIconDiv, {
          onAttach: (clonedElement, domIcon, domMarker) => {
            var clonedContent = clonedElement.getElementsByTagName("svg")[0];
            clonedContent.style.transform = `rotate(${heading - svgRotate}deg)`;
          },
        });
        var marker = new H.map.DomMarker({ lat, lng }, { icon });

        marker.addEventListener("tap", (evt) => {
          this.activeTrucker = trucker;
          this.$refs.activity.open();
        });

        marker.addEventListener(
          "pointerenter",
          (evt) => {
            this.markerName = `
              <b>${name}</b>
              <div>${timestamp} ${speed || 0}mph</div>
            `;
          },
          false,
        );

        marker.addEventListener(
          "pointerleave",
          (evt) => {
            this.markerName = null;
          },
          false,
        );

        marker.addEventListener(`driverMarker${trucker.id}`, (evt) => {
          this.hm.map.setCenter({ lat, lng });
          this.hm.map.setZoom(16);
          if (this.dispatchView) this.toggleDispatch();
        });

        trucker.marker = marker;
        this.hm.truckers.addObject(marker);
      });
      this.hm.group.addObject(this.hm.truckers);
      this.hm.map.addObject(this.hm.group);
    },
    initGoogleMap() {},
    addSpotsToGoogleMap() {},
    addTruckersToGoogleMap() {},
    initMap() {
      if (this.hereMap) this.initHereMap();
      if (this.googleMap) this.initGoogleMap();
    },
    async addSpotsToMap() {
      if (this.hereMap) await this.addSpotsToHereMap();
      // if (this.googleMap) this.addSpotsToGoogleMap();
    },
    async renderRouting(route) {
      if (this.hereMap) await this.renderRoutingToHereMap(route);
      // if(this.googleMap) await this.renderRoutingToGoogleMap(route);
    },
    addTruckersToMap() {
      if (this.hereMap) this.addTruckersToHereMap();
      // if (this.googleMap) this.addTruckersToGoogleMap();
    },
    autoTooltip() {
      this.mousemoveHandler = (evt) => {
        var div = document.getElementById("tooltip");
        if (div) {
          var { clientX, clientY } = evt;
          div.style.left = clientX + 10 + "px";
          div.style.top = clientY + 10 + "px";
        }
      };
      window.addEventListener("mousemove", this.mousemoveHandler);
    },
    cursorTimeline() {
      const dispatch = this.$refs.dispatch;
      const vselector = ".el-table__virtual-wrapper";
      this.cursorMousemoveHandler = (event) => {
        try {
          const tableWrapper = dispatch.querySelector(vselector);
          const rightCells = tableWrapper.querySelectorAll("td.right");
          const mouseX = event.clientX;
          let isInsideRightCell = false;
          rightCells.forEach((td) => {
            let tdRect = td.getBoundingClientRect();
            if (
              mouseX >= tdRect.left &&
              mouseX <= tdRect.right &&
              event.clientY >= tdRect.top &&
              event.clientY <= tdRect.bottom
            ) {
              isInsideRightCell = true;
            }
          });
          if (isInsideRightCell) {
            rightCells.forEach((td) => {
              let canvas = td.querySelector("canvas");
              let tdRect = td.getBoundingClientRect();
              if (!canvas) {
                const canvasContainer = document.createElement("div");
                canvasContainer.classList.add("canvas-container");
                td.appendChild(canvasContainer);
                canvas = document.createElement("canvas");
                canvas.width = td.offsetWidth;
                canvas.height = td.offsetHeight;
                canvasContainer.appendChild(canvas);
              }
              let ctx = canvas.getContext("2d");
              ctx.clearRect(0, 0, canvas.width, canvas.height);
              ctx.setLineDash([5, 5]); // Dashed line pattern
              ctx.beginPath();
              ctx.moveTo(mouseX - tdRect.left, 0);
              ctx.lineTo(mouseX - tdRect.left, td.offsetHeight);
              ctx.strokeStyle = "#254edb"; // Black color for the dashed line
              ctx.lineWidth = 1;
              ctx.stroke();
            });
          } else {
            rightCells.forEach((td) => {
              const canvas = td.querySelector("canvas");
              if (canvas) {
                const ctx = canvas.getContext("2d");
                ctx.clearRect(0, 0, canvas.width, canvas.height);
              }
            });
          }
        } catch (err) {
          console.log(err);
        }
      };
      document.addEventListener("mousemove", this.cursorMousemoveHandler);
    },
    mapViewClick() {
      this.mapClickHandler = (evt) => {
        if (!evt.target.closest(".panel")) {
          if (this.dispatchView) this.toggleDispatch();
        }
      };
      this.$refs.map.addEventListener("click", this.mapClickHandler);
    },
    showMarker(trucker) {
      if (trucker.marker) trucker.marker.dispatchEvent("tap");
    },
    focusDriverMarker(trucker) {
      if (trucker.marker)
        trucker.marker.dispatchEvent(
          new CustomEvent(`driverMarker${trucker.id}`),
        );
    },
    openActivity(trucker) {
      this.activeTrucker = trucker;
      this.$refs.activity.open();
    },
    openRoute(route) {
      this.activeRoute = route;
    },
    fitAllOnMap() {
      if (this.hereMap) {
        this.hm.map.getViewModel().setLookAtData({
          bounds: this.hm.group.getBoundingBox(),
        });
        setTimeout(() => this.hm.map.setZoom(this.hm.map.getZoom() - 0.5), 150);
      }
    },
    emptyGroup(group) {
      return group.getObjects().length == 0;
    },
    receive(data, target) {
      if (target == "location") this.receiveLocation(data);
    },
    receiveLocation(data) {
      let trucker = this.truckers.find((t) => t.id == data.company_id);
      if (trucker) {
        trucker.location = data;
        this.addTruckersToHereMap();
      }
    },
    close(target) {
      setTimeout(() => {
        if (target == "order") this.activeRoute = null;
        if (target == "spot") this.activeSpot = null;
      }, 100);
    },
  },
};
</script>
<style lang="scss">
.xmap {
  @apply w-full h-full relative bg-white flex flex-col;
  .u-table .el-table .cell {
    @apply p-0 #{!important};
  }

  & > .menu {
    @apply absolute bottom-1 left-1/2 transform -translate-x-1/2 z-50 bg-gray-800 px-6 py-1 rounded-full shadow-2xl flex items-center gap-6;
    a.link {
      @apply text-white no-underline flex flex-col text-xs items-center #{!important};
      .svg {
        @apply w-6 -mb-0.5;
      }
      &.active {
        @apply shadow-2xl text-blue-400 #{!important};
      }
    }
  }
  .map-view {
    @apply grow relative z-10;
    .dispatch {
      @apply absolute right-0 top-2 w-[260px] h-[calc(100%_-_60px)] overflow-auto p-1 border rounded shadow-2xl bg-gray-600 flex flex-col gap-1 z-50 opacity-90;
      .el-table__fixed::before,
      .el-table::before,
      .el-table::after {
        @apply bg-transparent #{!important};
      }
      .u-table {
        .el-table--border {
          @apply bg-gray-600 border-gray-700 #{!important};
        }
        .el-table__body-wrapper,
        .el-table__fixed-body-wrapper {
          @apply bg-gray-100;
        }
        .el-table__row td {
          @apply bg-gray-100 text-gray-800 #{!important};
        }
        th.left {
          @apply bg-gray-700 text-white #{!important};
          a {
            @apply text-white;
          }
          .el-date-editor {
            input {
              @apply text-white #{!important};
            }
            .el-input__icon {
              @apply text-white #{!important};
            }
          }
        }
        td.left {
          @apply bg-gray-600 text-white border-gray-700 #{!important};
          a {
            @apply text-white;
          }
          &.day_off,
          &.no_work {
            .panel {
              @apply blur-xs hover:blur-none #{!important};
            }
          }
        }
      }
      .left {
        .triggers {
          @apply flex items-center gap-1 p-1;
        }
        .link {
          @apply no-underline bg-black px-1 text-2xs font-bold text-gray-400 rounded #{!important};
          &.on {
            @apply text-green-500 #{!important};
          }
        }
        .hos-logs {
          @apply grid grid-cols-6 text-2xs bg-slate-700 p-1 m-1 rounded;
        }
      }

      .timelines {
        @apply flex items-center mx-4;
        & > div {
          @apply h-10 leading-10 relative;
          .hour {
            @apply absolute -left-1 top-0;
          }
          .scale {
            @apply flex items-center;
            div {
              @apply h-3 w-1/2 border-l border-l-gray-500;
            }
          }
        }
      }
      .timeline-cell {
        @apply relative h-10 mx-4;
      }
      .driver-name {
        @apply grow font-bold no-underline truncate text-gray-600 hover:underline;
      }
    }
    .order {
      @apply absolute top-2 left-2 z-50 w-80 shadow-2xl;
    }
    .spot {
      @apply absolute top-2 right-[265px] z-50 w-[640px] shadow-2xl;
    }
    .open {
      @apply absolute bottom-2 left-2 w-80 max-h-[40%] overflow-auto z-20 text-white rounded shadow-2xl bg-gray-600 empty:hidden;
      .undispatched {
        .title {
          @apply flex items-center justify-between text-white bg-red-500 leading-8 font-bold px-2 empty:hidden;
        }
      }
      .brokered {
        .title {
          @apply flex items-center justify-between text-white bg-purple-500 leading-8 font-bold px-2 empty:hidden;
        }
      }
      .trips {
        @apply flex flex-col gap-1 p-1;
        .trip {
          @apply relative h-20 p-1 bg-gray-700 rounded;
          .header {
            @apply text-xs bg-gray-900 p-1 flex items-center justify-between;
          }
          .route {
            @apply py-2;
            .actions {
              @apply pl-6;
            }
          }
        }
      }
    }
  }
}
.routes-count {
  @apply bg-gray-800 text-white px-0.5 text-xs rounded mr-1 w-5 h-4 leading-4 text-center;
}
.truck-icon {
  &.day_off,
  &.no_work {
    @apply opacity-40;
  }
}
.canvas-container {
  @apply absolute top-0 left-0 w-full h-full pointer-events-none z-50;
}
.map-filters {
  @apply bg-gray-700;
  .title {
    @apply text-gray-200 p-1 text-xs;
  }
  label.el-checkbox {
    @apply bg-gray-800 text-white border-gray-900 #{!important};
  }
  .el-checkbox-group {
    @apply flex flex-col gap-1;
    label {
      @apply m-0 #{!important};
    }
  }
}
#tooltip {
  @apply fixed bg-gray-800 text-white p-2 rounded z-20 empty:hidden;
}
@keyframes fadeInRight {
  0% {
    opacity: 0;
    transform: translateX(260px);
    width: 260px;
  }
  100% {
    opacity: 0.9;
    transform: translateX(0);
    width: calc(100% - 335px);
  }
}
@keyframes fadeOutRight {
  0% {
    opacity: 1;
    transform: translateX(0);
    width: calc(100% - 335px);
  }
  100% {
    opacity: 0.9;
    transform: translateX(0);
    width: 260px;
  }
}
.fade-in-right {
  animation: fadeInRight 0.75s forwards;
}
.fade-out-right {
  animation: fadeOutRight 0.75s forwards;
}
</style>
